import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { getToken, type TokenAudience } from '../utils/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const ACTIVITY_TREE_API_BASE_URL =
  import.meta.env.VITE_ACTIVITY_TREE_API_BASE_URL ?? API_BASE_URL;

const AUTH_ERROR_STATUSES = [401, 403];

/** Set on a request once it has been replayed, so we only ever retry once. */
type RetriableConfig = InternalAxiosRequestConfig & {
  retriedAfterAuthError?: boolean;
};

export type WithAuthOptions = {
  /** Which token audience signs this client's requests. */
  audience: TokenAudience;
  /** Prefix for the console error logged when a request finally fails. */
  logLabel: string;
  /**
   * Runs when a replayed request is rejected again — i.e. the failure isn't a
   * stale token and a fresh one won't fix it. Omit to leave the rejection to
   * the caller.
   */
  onUnrecoverable?: () => void;
};

/**
 * Attaches a bearer token to every request and, when the API rejects one,
 * force-refreshes the token and replays the request exactly once.
 *
 * A token can be rejected while still unexpired — revoked session, changed
 * roles or consent, clock skew — which is why the retry force-refreshes: it
 * bypasses MSAL's cached copy instead of resending the token just refused.
 * Without this the rejected token stayed in play until the user reloaded.
 */
export const withAuth = (
  client: AxiosInstance,
  { audience, logLabel, onUnrecoverable }: WithAuthOptions
): AxiosInstance => {
  client.interceptors.request.use(async (config) => {
    config.headers.Authorization = `Bearer ${await getToken(audience)}`;
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetriableConfig | undefined;
      const status = error.response?.status;

      if (config && status && AUTH_ERROR_STATUSES.includes(status)) {
        if (!config.retriedAfterAuthError) {
          config.retriedAfterAuthError = true;
          // Refresh first, then replay — the request interceptor re-runs on
          // `client.request` and picks up the newly cached token.
          await getToken(audience, { forceRefresh: true });
          return client.request(config);
        }
        onUnrecoverable?.();
      }

      // No response at all (network error, timeout, cancellation) also lands
      // here — `error.response` is undefined, so never read `.status` off it
      // without the optional chain.
      console.error(`${logLabel}:`, error);
      return Promise.reject(error);
    }
  );

  return client;
};

export const apiClient = withAuth(
  axios.create({
    baseURL: API_BASE_URL,
    timeout: 60000,
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  { audience: 'api', logLabel: 'API Error' }
);

// Keep the current opportunity number in the Axios default headers so that the
// request interceptor can automatically append oppNo to every /boq-versions call
// without each caller having to thread it through manually.
export const setOpportunityNo = (oppNo: string | undefined) => {
  if (oppNo) {
    apiClient.defaults.headers.common['opportunityNo'] = oppNo;
  } else {
    delete (apiClient.defaults.headers.common as Record<string, unknown>)['opportunityNo'];
  }
};

apiClient.interceptors.request.use((config) => {

  // For any /boq-versions request, automatically inject oppNo from the stored
  // opportunityNo header if the caller did not already supply it.
  if (config.url?.includes('boq-versions') && !config.params?.oppNo) {
    const oppNo = config.headers?.['opportunityNo'] ?? apiClient.defaults.headers.common['opportunityNo'];
    if (oppNo) {
      config.params = { ...config.params, oppNo };
    }
  }

  return config;
});

// Dedicated client for activity-tree endpoints, which live on a separate BFF host.
export const activityTreeApiClient = withAuth(
  axios.create({
    baseURL: ACTIVITY_TREE_API_BASE_URL,
    timeout: 60000,
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  { audience: 'api', logLabel: 'Activity Tree API Error' }
);

activityTreeApiClient.interceptors.request.use((config) => {
  return config;
});
